import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "./UserContext";
import "./css/Ai.css";

const API_BASE = "https://appwrite.hxabyte.com"; // set to ngrok url

function Ai() {
  const { getJWT } = useUser();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]); // {name, hash, chunks}
  const [selectedHash, setSelectedHash] = useState("");
  const [uploading, setUploading] = useState(false);

  const [question, setQuestion] = useState("");
  const [streaming, setStreaming] = useState(false);

  const [answer, setAnswer] = useState("");
  const [log, setLog] = useState("");

  const canAsk = useMemo(
    () => !!selectedHash && question.trim().length > 0 && !streaming,
    [selectedHash, question, streaming],
  );

  useEffect(() => {
    // auto-select first doc
    if (!selectedHash && files.length > 0) setSelectedHash(files[0].hash);
  }, [files, selectedHash]);
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        const base64 = result.split(",")[1];
        resolve(base64);
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  const onUpload = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      setLog("Upload error: Please upload a PDF under 3MB for now.");
      return;
    }
    setUploading(true);
    setLog("");

    try {
      const jwt = await getJWT();
      const pdfBase64 = await fileToBase64(file);

      const res = await fetch(`${API_BASE}/ai/rag/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fileName: file.name,
          pdfBase64,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Upload failed");

      setFiles((prev) => {
        const exists = prev.some((p) => p.hash === data.file_hash);
        if (exists) return prev;

        return [
          { name: file.name, hash: data.file_hash, chunks: data.chunk_count },
          ...prev,
        ];
      });

      setSelectedHash(data.file_hash);
      setLog(`Indexed: ${file.name} (${data.chunk_count} chunks)`);
    } catch (e) {
      setLog(`Upload error: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const askStream = async () => {
    if (!canAsk) return;

    setAnswer("");
    setStreaming(true);
    setLog("");

    try {
      const jwt = await getJWT();

      const res = await fetch(`${API_BASE}/ai/rag/ask`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question,
          file_hash: selectedHash,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Ask failed");

      setAnswer(data.answer || "");
    } catch (e) {
      setLog(`Ask error: ${e.message}`);
    } finally {
      setStreaming(false);
    }
  };
  const removeDoc = (hash) => {
    setFiles((prev) => prev.filter((f) => f.hash !== hash));
    if (selectedHash === hash) setSelectedHash("");
  };

  return (
    <div className="ai-wrap">
      <div className="ai-bg" />
      <div className="ai-tabs">
        <button className="ai-tab active">AI RAG</button>

        <button className="ai-tab" onClick={() => navigate("/ai-music")}>
          AI Music
        </button>
      </div>

      <h2>AI RAG</h2>
      <header className="ai-header">
        <div className="ai-brand">
          <div className="ai-badge">HX</div>
          <div>
            <div className="ai-title">HXABYTE AI LAB</div>
            <div className="ai-subtitle">
              RAG • Streaming • Multi-file Memory
            </div>
          </div>
        </div>

        <div className="ai-status">
          <span className={`dot ${streaming ? "dot-live" : "dot-idle"}`} />
          <span>{streaming ? "STREAMING" : "READY"}</span>
        </div>
      </header>

      <main className="ai-grid">
        {/* Left: Documents */}
        <section className="panel">
          <div className="panel-title">Documents</div>

          <label className={`upload ${uploading ? "disabled" : ""}`}>
            <input
              type="file"
              accept=".pdf"
              onChange={(e) => onUpload(e.target.files?.[0])}
              disabled={uploading}
            />
            <span>{uploading ? "Indexing..." : "Upload PDF"}</span>
            <span className="hint">Uploads PDF for Gemini Q&A</span>
          </label>

          <div className="doc-list">
            {files.length === 0 ? (
              <div className="empty">Upload a PDF to start.</div>
            ) : (
              files.map((f) => (
                <button
                  key={f.hash}
                  className={`doc ${selectedHash === f.hash ? "active" : ""}`}
                  onClick={() => setSelectedHash(f.hash)}
                  title={f.hash}
                >
                  <div className="doc-name">{f.name}</div>
                  <div className="doc-meta">
                    <span>{f.chunks ?? "?"} chunks</span>
                    <span className="sep">•</span>
                    <span className="hash">{f.hash.slice(0, 8)}</span>
                  </div>
                  <div
                    className="doc-x"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      removeDoc(f.hash);
                    }}
                    role="button"
                    aria-label="remove"
                  >
                    ✕
                  </div>
                </button>
              ))
            )}
          </div>

          {log ? <div className="log">{log}</div> : null}
        </section>

        {/* Right: Chat */}
        <section className="panel chat">
          <div className="panel-title">Chat</div>

          <div className="chat-window">
            {answer ? (
              <div className="bubble">
                <div className="bubble-head">Answer</div>
                <div className="bubble-body">
                  <div className="para">
                    {answer}
                    {streaming && <span className="cursor">▍</span>}
                  </div>
                  {streaming && <div className="cursor">▍</div>}
                </div>
              </div>
            ) : (
              <div className="empty">
                Ask a question after selecting a document.
                {streaming ? " (Streaming...)" : ""}
              </div>
            )}
          </div>

          <div className="composer">
            <input
              className="q"
              placeholder={
                selectedHash
                  ? "Ask something about the selected PDF..."
                  : "Upload/select a PDF first"
              }
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={!selectedHash}
            />
            <button
              className={`send ${canAsk ? "" : "disabled"}`}
              onClick={askStream}
              disabled={!canAsk}
            >
              {streaming ? "…" : "Send"}
            </button>
          </div>

          <div className="meta">
            <span>
              Selected:{" "}
              {selectedHash ? selectedHash.slice(0, 12) + "…" : "None"}
            </span>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Ai;
